import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  File, 
  Image, 
  Video, 
  FileText, 
  Music, 
  Archive, 
  CheckCircle, 
  AlertCircle, 
  Loader,
  Trash2,
  Eye,
  Download,
  Copy,
  Share2
} from 'lucide-react';
import { uploadFileWithProgress, deleteFile } from '../firebase/r2storage';

const FileUpload = ({
  onFilesUploaded,
  onFileRemoved,
  multiple = true,
  accept = '*/*',
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 10,
  storagePath = 'uploads',
  showPreview = true,
  showProgress = true,
  showActions = true,
  className = '',
  disabled = false,
  placeholder = 'Drag and drop files here, or click to select',
  allowedTypes = ['image/*', 'video/*', 'application/pdf', 'text/*', 'application/*'],
  customValidation = null
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const fileInputRef = useRef(null);

  // File type icons mapping
  const getFileIcon = (file) => {
    const type = file?.type || '';
    if (type.startsWith('image/')) return <Image className="w-6 h-6" />;
    if (type.startsWith('video/')) return <Video className="w-6 h-6" />;
    if (type.startsWith('audio/')) return <Music className="w-6 h-6" />;
    if (type === 'application/pdf') return <FileText className="w-6 h-6" />;
    if (type.includes('zip') || type.includes('rar') || type.includes('tar')) return <Archive className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Validate file
  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)} limit`;
    }

    // Check file type
    const isAllowedType = allowedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file?.type?.startsWith(type.slice(0, -1));
      }
      return file?.type === type;
    });

    if (!isAllowedType) {
      return `File type ${file?.type || 'unknown'} is not allowed`;
    }

    // Custom validation
    if (customValidation) {
      const customError = customValidation(file);
      if (customError) return customError;
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    
    // Check max files limit
    if (files.length + fileArray.length > maxFiles) {
      return;
    }

    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push({ file, error });
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      errors.forEach(({ file, error }) => {
        });
    }

    if (validFiles.length > 0) {
      const newFiles = multiple ? [...files, ...validFiles] : validFiles;
      setFiles(newFiles);
      
      // Auto upload if not multiple selection
      if (!multiple) {
        uploadFiles(validFiles);
      }
    }
  }, [files, multiple, maxFiles, maxSize, allowedTypes, customValidation]);

  // Upload files to Firebase Storage
  const uploadFiles = async (filesToUpload = files) => {
    if (filesToUpload.length === 0) return;

    setUploading(true);
    setUploadProgress({});
    setUploadErrors({});

    const uploadPromises = filesToUpload.map(async (file) => {
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `${storagePath}/${fileName}`;
      
      return new Promise((resolve, reject) => {
        uploadFileWithProgress(
          file,
          filePath,
          (progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress
            }));
          },
          (error) => {
            setUploadErrors(prev => ({
              ...prev,
              [file.name]: error.message
            }));
            reject({ file, error });
          },
          (url, metadata) => {
            resolve({
              file,
              url: url,
              path: filePath,
              metadata: {
                name: file.name,
                size: file.size,
                type: file.type,
                uploadedAt: new Date().toISOString()
              }
            });
          }
        );
      });
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(result => !result.error);
      
      if (successfulUploads.length > 0) {
        onFilesUploaded?.(successfulUploads);
      }
      
      // Remove uploaded files from local state
      setFiles(prev => prev.filter(file => 
        !filesToUpload.some(uploadFile => uploadFile.name === file.name)
      ));
      
    } catch (error) {
      } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  // Remove file from local state
  const removeFile = (fileToRemove) => {
    setFiles(prev => prev.filter(file => file !== fileToRemove));
    onFileRemoved?.(fileToRemove);
  };

  // Delete file from R2 Storage
  const deleteFileFromStorage = async (filePath) => {
    try {
      await deleteFile(filePath);
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // Copy file URL to clipboard
  const copyFileUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here
    } catch (error) {
      }
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  return (
    <div className={`file-upload-container ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />
        
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400 mb-2">{placeholder}</p>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          Max size: {formatFileSize(maxSize)} • Max files: {maxFiles}
        </p>
        
        {dragActive && (
          <div className="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-lg flex items-center justify-center">
            <p className="text-blue-600 font-medium">Drop files here</p>
          </div>
        )}
      </div>

      {/* File List */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 space-y-2"
          >
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Selected Files ({files.length})
            </h3>
            
            {files.map((file, index) => (
              <motion.div
                key={`${file.name}-${index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getFileIcon(file)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {showProgress && uploadProgress[file.name] !== undefined && (
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[file.name]}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {Math.round(uploadProgress[file.name])}%
                      </span>
                    </div>
                  )}

                  {uploadErrors[file.name] && (
                    <div className="flex items-center space-x-1 text-red-600">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-xs">{uploadErrors[file.name]}</span>
                    </div>
                  )}

                  {showActions && (
                    <>
                      {showPreview && file?.type?.startsWith('image/') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            try {
                              if (file instanceof File || file instanceof Blob) {
                                window.open(URL.createObjectURL(file), '_blank');
                              } else {
                              }
                            } catch (error) {
                            }
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(file);
                        }}
                        className="p-1 text-red-400 hover:text-red-600 dark:hover:text-red-300"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Upload Button */}
            {multiple && files.length > 0 && (
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={() => setFiles([])}
                  className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Clear All
                </button>
                <button
                  onClick={() => uploadFiles()}
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {uploading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Files
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// File Preview Component
export const FilePreview = ({ file, onDelete, onCopy, onDownload, showActions = true }) => {
  const [previewUrl, setPreviewUrl] = useState(null);

  // File type icons mapping
  const getFileIcon = (file) => {
    // Handle both direct file objects and uploaded file objects with metadata
    const type = file?.type || file?.metadata?.type || '';
    if (type.startsWith('image/')) return <Image className="w-6 h-6" />;
    if (type.startsWith('video/')) return <Video className="w-6 h-6" />;
    if (type.startsWith('audio/')) return <Music className="w-6 h-6" />;
    if (type === 'application/pdf') return <FileText className="w-6 h-6" />;
    if (type.includes('zip') || type.includes('rar') || type.includes('tar')) return <Archive className="w-6 h-6" />;
    return <File className="w-6 h-6" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  React.useEffect(() => {
    const fileType = file?.type || file?.metadata?.type || '';
    if (fileType.startsWith('image/')) {
      // Handle both direct file objects and uploaded file objects
      const fileObj = file.file || file;
      if (fileObj && typeof fileObj === 'object' && 'type' in fileObj) {
        try {
          // Validate that fileObj is a proper File or Blob before creating object URL
          if (fileObj instanceof File || fileObj instanceof Blob) {
            const url = URL.createObjectURL(fileObj);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
          } else {
          }
        } catch (error) {
          setPreviewUrl(null);
        }
      }
    }
  }, [file]);

  return (
    <div className="relative group">
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {previewUrl ? (
          <img 
            src={previewUrl} 
            alt={file?.name || file?.metadata?.name || 'File'}
            className="w-full h-32 object-cover"
          />
        ) : (
          <div className="w-full h-32 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            {getFileIcon(file)}
          </div>
        )}
        
        {showActions && (
          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
            <button
              onClick={() => onDelete?.(file)}
              className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onCopy?.(file)}
              className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
              title="Copy URL"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDownload?.(file)}
              className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      <div className="p-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {file?.name || file?.metadata?.name || 'Unknown File'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatFileSize(file?.size || file?.metadata?.size || 0)}
        </p>
      </div>
    </div>
  );
};

// File Upload Hook
export const useFileUpload = (options = {}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});

  const uploadFile = async (file, customPath = '') => {
    setUploading(true);
    
    const fileName = `${Date.now()}_${file.name}`;
    const filePath = `${options.storagePath || 'uploads'}/${customPath}/${fileName}`;
    
    return new Promise((resolve, reject) => {
      uploadFileWithProgress(
        file,
        filePath,
        (progressValue) => {
          setProgress(prev => ({
            ...prev,
            [file.name]: progressValue
          }));
        },
        (error) => {
          setUploading(false);
          reject(error);
        },
        (url, metadata) => {
          const fileData = {
            name: file.name,
            url: url,
            path: filePath,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString()
          };
          
          setUploadedFiles(prev => [...prev, fileData]);
          setUploading(false);
          resolve(fileData);
        }
      );
    });
  };

  const removeFile = async (filePath) => {
    try {
      await deleteFile(filePath);
      setUploadedFiles(prev => prev.filter(file => file.path !== filePath));
    } catch (error) {
      console.error('Error removing file:', error);
    }
  };

  return {
    uploadedFiles,
    uploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  };
};

export default FileUpload;
